// Interfaces
import { IMappedTree, ITreeItem, ITreeIdentifiers, ITreeLeaf, ITreeSelection } from "./interfaces";

// Constants
const leafAddedSpace = 12;
const spacing = 6.5;

const isAllowedTreeKey = (key: string) => !["leaf", "referenceId"].includes(key);

const getPrecisionLevel = (level = 0, isLeaf = false, isRoot = false) => {
	const add = isLeaf ? leafAddedSpace : 0;
	const rootAdd = isRoot ? spacing * 2 - 2 : 0;
	const padding = spacing * level + add + rootAdd;
	return `${padding}px`;
};

const getItemId = (parent = "", level = "", key = 0) => {
	let identifier = `${parent}_${level}_${key}`;
	identifier = identifier.replace(/(^_+|_+$)/g, "");
	identifier = identifier.replace(/_+/g, "_");
	return identifier;
};

const getMappedTree = ({ tree = [], selectBy = "_id", level = "", mappedTree = new Map() }: IMappedTree) => {
	tree.forEach((item: { list?: object[] }, key) => {
		const isLeaf = !item?.list?.length;
		const itemKey = getItemId(level, item?.[selectBy as keyof object], key);

		if (isLeaf) {
			mappedTree.set(itemKey, item);
		} else {
			getMappedTree({ tree: item.list as never[], level: itemKey, selectBy, mappedTree });
		}
	});

	return mappedTree;
};

const getTreeIdentifiers = ({ tree = [], selectBy = "_id", level = "", mappedTree = {} }: ITreeIdentifiers) => {
	if (!tree?.length) {
		return {
			referenceId: "",
			leaf: true,
		};
	}

	tree.forEach((item: ITreeItem, key) => {
		const isLeaf = !item?.list?.length;
		const itemId = item?.[selectBy as keyof object] ?? "";
		const itemKey = getItemId(level, itemId, key);
		if (itemKey) {
			if (isLeaf) {
				mappedTree[itemKey as keyof object] = {
					referenceId: itemId,
					leaf: true,
				};
			} else {
				const leafs = getTreeIdentifiers({ tree: item.list as object[], level: itemKey, selectBy });
				mappedTree[itemKey as keyof object] = {
					...leafs,
					referenceId: itemId,
					leaf: false,
				};
			}
		}
	});
	return mappedTree;
};

const getTreeItem = (tree: object[] = [], selectBy = "_id", selectedId = "") => {
	if (!tree?.length || !selectedId) {
		return null;
	}
	const mappedTree = getMappedTree({ tree, selectBy });
	return mappedTree.get(selectedId) ?? null;
};

const findRootTree = (tree: object | null = {}, root = ""): object | null => {
	if (!tree || !root) {
		return null;
	}

	if (root in tree) {
		return tree[root as keyof object];
	}

	for (const item of Object.keys(tree)) {
		if (!isAllowedTreeKey(item)) {
			return null;
		}

		const rootTree = findRootTree(tree[item as keyof object], root);
		if (rootTree) {
			return rootTree;
		}
	}

	return null;
};

const findInTreePath = (tree: object | null = {}, leaf = "") => {
	if (!tree || !leaf || leaf === "leaf" || leaf === "referenceId") {
		return false;
	}

	if (leaf in tree) {
		return true;
	}

	for (const item of Object.keys(tree)) {
		if (isAllowedTreeKey(item)) {
			const foundLeaf = findInTreePath(tree[item as keyof object], leaf);
			if (foundLeaf) {
				return true;
			}
		}
	}

	return false;
};

const findInTreeLeaf = (tree: object | null = {}, reference = ""): ITreeLeaf | null => {
	if (!tree || !reference) {
		return null;
	}

	for (const item of Object.keys(tree)) {
		if (isAllowedTreeKey(item)) {
			const element = tree[item as keyof object] as ITreeLeaf;
			const idRef = element?.referenceId ?? "";
			if (idRef === reference) {
				return {
					...(tree[item as keyof object] as object),
					treeId: item,
				} as ITreeLeaf;
			}

			const leaf: object | null = findInTreeLeaf(tree[item as keyof object], reference);
			if (leaf) {
				return leaf;
			}
		}
	}

	return null;
};

const findAllInTreeLeaf = (tree: object | null = {}, reference = "", results: ITreeLeaf[] = []): ITreeLeaf[] => {
	if (!tree || !reference) {
		return results;
	}

	for (const item of Object.keys(tree)) {
		if (isAllowedTreeKey(item)) {
			const element = tree[item as keyof object] as ITreeLeaf;
			const idRef = element?.referenceId ?? "";
			if (idRef === reference) {
				const occurrence = {
					...(tree[item as keyof object] as object),
					treeId: item,
				} as ITreeLeaf;
				results.push(occurrence);
			}
			findAllInTreeLeaf(tree[item as keyof object], reference, results);
		}
	}

	return results;
};

const isLeafInPath = (tree: object | null = {}, root = "", leafs: string[] = []) => {
	if (!tree || !root || !leafs?.length) {
		return false;
	}
	const path = findRootTree(tree, root);
	return leafs.some((leaf) => findInTreePath(path, leaf));
};

const getSelectedTreeId = (tree: object | null = {}, selectedId = ""): string => {
	if (!tree || !selectedId) {
		return "";
	}
	const leaf = findInTreeLeaf(tree, selectedId);
	return leaf?.treeId ?? "";
};

const getSelectedTreeIds = (tree: object | null = {}, nodes: string[] = []): string[] => {
	const foundNodes = nodes.reduce((acc: ITreeLeaf[], node: string) => {
		const occurrence = findAllInTreeLeaf(tree, node);
		return [...acc, ...occurrence];
	}, []);

	return foundNodes.reduce((acc: string[], item: ITreeLeaf) => {
		const isPresent = acc.find((treeId) => treeId === item.treeId);
		if (!isPresent && item?.treeId) {
			acc = [...acc, item.treeId];
		}
		return acc;
	}, []);
};

const getSelectedNodes = ({ tree = {}, selectedIds = [], nodes = [], isMultiple = false }: ITreeSelection): string[] => {
	if (!nodes?.length || selectedIds?.length) {
		return selectedIds;
	}

	let nodesSelected = nodes.map((node) => getSelectedTreeId(tree, node));
	nodesSelected = nodesSelected.filter((node) => node);

	if (isMultiple) {
		const allSelectedNodes = getSelectedTreeIds(tree, nodes);
		if (allSelectedNodes?.length) {
			return allSelectedNodes;
		}
	}

	if (nodesSelected?.length) {
		return nodesSelected;
	}

	return selectedIds;
};

const getParentPathNodes = (tree: object | null = {}, nodes: string[] = [], results: string[] = []): string[] => {
	if (!tree || !nodes?.length) {
		return results;
	}

	for (const item of Object.keys(tree)) {
		if (isAllowedTreeKey(item)) {
			const { referenceId = "", leaf = false } = tree[item as keyof object] || {};
			if (referenceId === nodes[0] && !leaf) {
				results.push(item);
				nodes.shift();
				return getParentPathNodes(tree[item as keyof object], nodes, results);
			}
		}
	}

	return [];
};

export {
	findRootTree,
	getItemId,
	getSelectedNodes,
	getParentPathNodes,
	getPrecisionLevel,
	getMappedTree,
	getTreeItem,
	getSelectedTreeId,
	getTreeIdentifiers,
	isLeafInPath,
};
